import React, {useCallback, useEffect, useMemo, useState} from "react";
import {useSelector} from "react-redux";
import {useNavigate, useSearchParams} from "react-router-dom";
import {Button} from "primereact/button";
import {Column} from "primereact/column";
import {DataTable} from "primereact/datatable";
import {Dialog} from "primereact/dialog";
import {InputText} from "primereact/inputtext";
import {Message} from "primereact/message";
import {TabPanel, TabView} from "primereact/tabview";
import {Tag} from "primereact/tag";
import PageHeaderButton from "../components/page-header-button";
import AdminAccessDenied from "../../system/components/admin-access-denied";
import MarkdownHelp from "../../system/components/markdown-help";
import {useContentTypeData} from "../../system/use-content-type-data";
import {CONTENT_TYPE_UIDS} from "../../system/content-type-registry";
import {
    ADMIN_PERMISSIONS,
    hasAdminPermission,
    hasOperationsAccess,
} from "../../system/admin-access";
import {useTranslate} from "../../system/useTranslate";
import {parseDate} from "../../system/utils";
import {
    DataState,
    RTL_DATATABLE_PROPS,
    SalesSectionHeader,
    StatCard,
    TableCard,
} from "../sales/components/common";
import {statusSeverity} from "../sales/sales-utils";
import {
    buildFinancialDiagnostics,
    buildWorkspaceHealth,
    entityId,
    entityLabel,
    formatMoney,
    isCurrentSubscription,
    statusCount,
    workspaceIdOf,
} from "./operations-utils";

const ALL_QUERY = {maxPages: 25, pageSize: 200, sort: "createdAt:DESC"};
const RECENT_QUERY = {page: 1, pageSize: 100, sort: "createdAt:DESC"};

function useCollectionLoader(controls, enabled = true) {
    const load = useCallback(async (force = false) => {
        if (!enabled) return;
        await Promise.allSettled(
            controls.map((control) => (
                (force ? control.reload : control.ensure)(ALL_QUERY, {all: true})
            ))
        );
    }, [controls, enabled]);

    useEffect(() => {
        load().catch(() => undefined);
    }, [load]);

    return load;
}

function CollectionErrors({errors}) {
    const translate = useTranslate();
    if (!errors.length) return null;

    return (
        <Message
            className="w-full text-xs"
            severity="warn"
            text={translate("operations-partial-data-warning")}
            pt={{text: {className: "text-xs"}}}
        />
    );
}

function HealthTag({value}) {
    const translate = useTranslate();
    const severity = value === "healthy" ? "success" : value === "warning" ? "warning" : "danger";
    return (
        <Tag
            className="text-xs"
            severity={severity}
            value={translate(`operations-health-${value}`)}
        />
    );
}

function SeverityTag({value}) {
    const translate = useTranslate();
    return (
        <Tag
            className="text-xs"
            severity={value === "critical" ? "danger" : "warning"}
            value={translate(`operations-severity-${value}`)}
        />
    );
}

function OperationsOverview({embedded = false, onOpenTab}) {
    const translate = useTranslate();
    const navigate = useNavigate();
    const adminUser = useSelector((state) => state.auth.user);
    const can = (permission) => hasAdminPermission(adminUser, permission);
    const users = useContentTypeData(CONTENT_TYPE_UIDS.USER);
    const workspaces = useContentTypeData(CONTENT_TYPE_UIDS.WORKSPACE);
    const memberships = useContentTypeData(CONTENT_TYPE_UIDS.WORKSPACE_MEMBERSHIP);
    const subscriptions = useContentTypeData(CONTENT_TYPE_UIDS.SUBSCRIPTION);
    const activities = useContentTypeData(CONTENT_TYPE_UIDS.USER_EVENT_LOG);
    const controls = useMemo(
        () => [
            {ensure: users.ensure, reload: users.reload},
            {ensure: workspaces.ensure, reload: workspaces.reload},
            {ensure: memberships.ensure, reload: memberships.reload},
            {ensure: subscriptions.ensure, reload: subscriptions.reload},
        ],
        [
            memberships.ensure,
            memberships.reload,
            subscriptions.ensure,
            subscriptions.reload,
            users.ensure,
            users.reload,
            workspaces.ensure,
            workspaces.reload,
        ]
    );
    const loadCollections = useCollectionLoader(controls);
    const ensureActivities = activities.ensure;
    const reloadActivities = activities.reload;

    const loadActivities = useCallback(async (force = false) => {
        const request = force ? reloadActivities : ensureActivities;
        await request({page: 1, pageSize: 25, sort: "createdAt:DESC"});
    }, [ensureActivities, reloadActivities]);

    useEffect(() => {
        loadActivities().catch(() => undefined);
    }, [loadActivities]);

    const usersAvailable = users.status === "succeeded";
    const workspacesAvailable = workspaces.status === "succeeded";
    const membershipsAvailable = memberships.status === "succeeded";
    const subscriptionsAvailable = subscriptions.status === "succeeded";
    const activitiesAvailable = activities.status === "succeeded";
    const activeWorkspaces = workspacesAvailable
        ? statusCount(workspaces.items, "workspaceStatus", "active")
        : null;
    const blockedUsers = usersAvailable
        ? users.items.filter((user) => user.blocked).length
        : null;
    const suspendedWorkspaces = workspacesAvailable
        ? statusCount(workspaces.items, "workspaceStatus", "suspended")
        : null;
    const membershipsWithoutMember = membershipsAvailable
        ? memberships.items.filter((membership) => !entityId(membership.member)).length
        : null;
    const subscribedWorkspaceIds = new Set(
        subscriptions.items
            .filter((subscription) => isCurrentSubscription(subscription))
            .map((subscription) => String(workspaceIdOf(subscription)))
    );
    const workspacesWithoutSubscription = workspacesAvailable && subscriptionsAvailable
        ? workspaces.items.filter(
            (workspace) => !subscribedWorkspaceIds.has(String(entityId(workspace)))
        ).length
        : null;
    const pendingResponses = activitiesAvailable
        ? activities.items.filter(
            (activity) => activity.requiresResponse && activity.responseState === "waiting"
        ).length
        : null;
    const alerts = [
        {
            code: "workspaces-without-subscription",
            value: workspacesWithoutSubscription,
            severity: workspacesWithoutSubscription ? "warning" : "success",
            route: "/operations",
            tab: "health",
            permission: ADMIN_PERMISSIONS.OPERATIONS_TAB_HEALTH,
            available: workspacesWithoutSubscription !== null,
        },
        {
            code: "memberships-without-member",
            value: membershipsWithoutMember,
            severity: membershipsWithoutMember ? "danger" : "success",
            route: "/operations",
            tab: "health",
            permission: ADMIN_PERMISSIONS.OPERATIONS_TAB_HEALTH,
            available: membershipsWithoutMember !== null,
        },
        {
            code: "suspended-workspaces",
            value: suspendedWorkspaces,
            severity: suspendedWorkspaces ? "warning" : "success",
            route: "/workspaces",
            permission: ADMIN_PERMISSIONS.WORKSPACE_VIEW,
            available: suspendedWorkspaces !== null,
        },
        {
            code: "blocked-users",
            value: blockedUsers,
            severity: blockedUsers ? "warning" : "success",
            route: "/users",
            permission: ADMIN_PERMISSIONS.USER_VIEW,
            available: blockedUsers !== null,
        },
        {
            code: "pending-responses",
            value: pendingResponses,
            severity: pendingResponses ? "danger" : "success",
            route: "/operations",
            tab: "activity",
            permission: ADMIN_PERMISSIONS.OPERATIONS_TAB_ACTIVITY,
            available: pendingResponses !== null,
        },
    ].filter((alert) => alert.available && can(alert.permission));
    const collectionLoading = users.loading
        || workspaces.loading
        || memberships.loading
        || subscriptions.loading;
    const collectionErrors = [
        users.error,
        workspaces.error,
        memberships.error,
        subscriptions.error,
        activities.error,
    ].filter(Boolean);
    const loading = collectionLoading || activities.loading;

    return (
        <div className={`flex flex-column gap-3 ${embedded ? "" : "w-full"}`}>
            <CollectionErrors errors={collectionErrors}/>

            <div className="grid">
                <StatCard
                    label={translate("operations-total-users")}
                    value={users.loading ? "..." : usersAvailable ? users.total : "-"}
                    icon="pi pi-users"
                    tone="blue"
                />
                <StatCard
                    label={translate("operations-active-workspaces")}
                    value={workspaces.loading ? "..." : activeWorkspaces ?? "-"}
                    icon="pi pi-building"
                    tone="green"
                />
                <StatCard
                    label={translate("operations-workspaces-without-subscription")}
                    value={subscriptions.loading || workspaces.loading ? "..." : workspacesWithoutSubscription ?? "-"}
                    icon="pi pi-id-card"
                    tone={workspacesWithoutSubscription ? "red" : "green"}
                />
                <StatCard
                    label={translate("operations-pending-responses")}
                    value={activities.loading ? "..." : pendingResponses ?? "-"}
                    icon="pi pi-inbox"
                    tone={pendingResponses ? "red" : "green"}
                />
            </div>

            <div className="grid">
                <div className="col-12 xl:col-8">
                    <TableCard>
                        <div className="p-3 flex flex-column gap-3">
                            <SalesSectionHeader
                                title={translate("operations-alerts")}
                                description={translate("operations-alerts-description")}
                                actions={(
                                    <Button
                                        icon="pi pi-refresh"
                                        label={translate("refresh")}
                                        className="text-xs gap-2"
                                        text
                                        loading={loading}
                                        onClick={() => {
                                            loadCollections(true);
                                            loadActivities(true);
                                        }}
                                    />
                                )}
                            />
                            <div className="flex flex-column gap-2">
                                {alerts.map((alert) => (
                                    <div
                                        key={alert.code}
                                        className="flex align-items-center justify-content-between gap-3 p-3 border-1 border-200 border-round surface-50"
                                    >
                                        <div className="flex align-items-center gap-2">
                                            <Tag
                                                className="text-xs"
                                                severity={alert.severity}
                                                value={alert.value}
                                            />
                                            <span className="text-xs text-800">
                                                {translate(`operations-alert-${alert.code}`)}
                                            </span>
                                        </div>
                                        <Button
                                            icon="pi pi-arrow-left"
                                            aria-label={translate("view")}
                                            className="text-xs gap-2"
                                            text
                                            rounded
                                            onClick={() => {
                                                if (alert.tab && onOpenTab) onOpenTab(alert.tab);
                                                else navigate(alert.tab
                                                    ? `${alert.route}?tab=${alert.tab}`
                                                    : alert.route
                                                );
                                            }}
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                    </TableCard>
                </div>

                <div className="col-12 xl:col-4">
                    <TableCard>
                        <div className="p-3 flex flex-column gap-3">
                            <SalesSectionHeader
                                title={translate("operations-quick-actions")}
                                description={translate("operations-quick-actions-description")}
                            />
                            {can(ADMIN_PERMISSIONS.USER_VIEW) && <Button
                                icon="pi pi-search"
                                label={translate("operations-find-user")}
                                className="text-xs gap-2 w-full"
                                outlined
                                onClick={() => navigate("/users")}
                            />}
                            {can(ADMIN_PERMISSIONS.WORKSPACE_VIEW) && <Button
                                icon="pi pi-building"
                                label={translate("operations-find-workspace")}
                                className="text-xs gap-2 w-full"
                                outlined
                                onClick={() => navigate("/workspaces")}
                            />}
                            {can(ADMIN_PERMISSIONS.OPERATIONS_TAB_HEALTH) && <Button
                                icon="pi pi-heart"
                                label={translate("operations-run-health-review")}
                                className="text-xs gap-2 w-full"
                                outlined
                                onClick={() => {
                                    if (onOpenTab) onOpenTab("health");
                                    else navigate("/operations?tab=health");
                                }}
                            />}
                        </div>
                    </TableCard>
                </div>
            </div>
        </div>
    );
}

function WorkspaceHealthTab() {
    const translate = useTranslate();
    const navigate = useNavigate();
    const adminUser = useSelector((state) => state.auth.user);
    const canOpenWorkspace = hasAdminPermission(adminUser, ADMIN_PERMISSIONS.WORKSPACE_VIEW);
    const canPreviewRepair = hasAdminPermission(adminUser, ADMIN_PERMISSIONS.WORKSPACE_REPAIR);
    const workspaces = useContentTypeData(CONTENT_TYPE_UIDS.WORKSPACE);
    const memberships = useContentTypeData(CONTENT_TYPE_UIDS.WORKSPACE_MEMBERSHIP);
    const roles = useContentTypeData(CONTENT_TYPE_UIDS.USER_ROLE);
    const subscriptions = useContentTypeData(CONTENT_TYPE_UIDS.SUBSCRIPTION);
    const wallets = useContentTypeData(CONTENT_TYPE_UIDS.CREDIT_WALLET);
    const aiSettings = useContentTypeData(CONTENT_TYPE_UIDS.WORKSPACE_AI_SETTING);
    const controls = useMemo(
        () => [
            {ensure: workspaces.ensure, reload: workspaces.reload},
            {ensure: memberships.ensure, reload: memberships.reload},
            {ensure: roles.ensure, reload: roles.reload},
            {ensure: subscriptions.ensure, reload: subscriptions.reload},
            {ensure: wallets.ensure, reload: wallets.reload},
            {ensure: aiSettings.ensure, reload: aiSettings.reload},
        ],
        [
            aiSettings.ensure,
            aiSettings.reload,
            memberships.ensure,
            memberships.reload,
            roles.ensure,
            roles.reload,
            subscriptions.ensure,
            subscriptions.reload,
            wallets.ensure,
            wallets.reload,
            workspaces.ensure,
            workspaces.reload,
        ]
    );
    const loadCollections = useCollectionLoader(controls);
    const [query, setQuery] = useState("");
    const [preview, setPreview] = useState(null);
    const healthRows = useMemo(() => buildWorkspaceHealth({
        workspaces: workspaces.items,
        memberships: memberships.status === "succeeded" ? memberships.items : null,
        roles: roles.status === "succeeded" ? roles.items : null,
        subscriptions: subscriptions.status === "succeeded" ? subscriptions.items : null,
        wallets: wallets.status === "succeeded" ? wallets.items : null,
        aiSettings: aiSettings.status === "succeeded" ? aiSettings.items : null,
    }), [
        aiSettings.items,
        aiSettings.status,
        memberships.items,
        memberships.status,
        roles.items,
        roles.status,
        subscriptions.items,
        subscriptions.status,
        wallets.items,
        wallets.status,
        workspaces.items,
    ]);
    const normalizedQuery = query.trim().toLowerCase();
    const visibleRows = normalizedQuery
        ? healthRows.filter((row) => (
            String(row.name || "").toLowerCase().includes(normalizedQuery)
            || String(row.documentId || "").toLowerCase().includes(normalizedQuery)
            || row._issues.some((issue) => issue.code.includes(normalizedQuery))
        ))
        : healthRows;
    const healthy = healthRows.filter((row) => row._health === "healthy").length;
    const warnings = healthRows.filter((row) => row._health === "warning").length;
    const critical = healthRows.filter((row) => row._health === "critical").length;
    const loading = workspaces.loading
        || memberships.loading
        || roles.loading
        || subscriptions.loading
        || wallets.loading
        || aiSettings.loading;
    const errors = [
        workspaces.error,
        memberships.error,
        roles.error,
        subscriptions.error,
        wallets.error,
        aiSettings.error,
    ].filter(Boolean);

    return (
        <div className="flex flex-column gap-3">
            <CollectionErrors errors={errors}/>
            <div className="grid">
                <StatCard label={translate("operations-health-total")} value={healthRows.length} icon="pi pi-building" tone="blue"/>
                <StatCard label={translate("operations-health-healthy")} value={healthy} icon="pi pi-check-circle" tone="green"/>
                <StatCard label={translate("operations-health-warning")} value={warnings} icon="pi pi-exclamation-triangle" tone="orange"/>
                <StatCard label={translate("operations-health-critical")} value={critical} icon="pi pi-times-circle" tone="red"/>
            </div>
            <TableCard>
                <div className="p-3 flex flex-column gap-3">
                    <SalesSectionHeader
                        title={translate("operations-workspace-health-title")}
                        description={translate("operations-workspace-health-description")}
                        actions={(
                            <Button
                                icon="pi pi-refresh"
                                label={translate("refresh")}
                                className="text-xs gap-2"
                                text
                                loading={loading}
                                onClick={() => loadCollections(true)}
                            />
                        )}
                    />
                    <InputText
                        className="w-full md:w-24rem text-xs"
                        value={query}
                        placeholder={translate("operations-health-search")}
                        onChange={(event) => setQuery(event.target.value)}
                    />
                    <DataState loading={loading} error={null} empty={!visibleRows.length}>
                        <DataTable
                            {...RTL_DATATABLE_PROPS}
                            value={visibleRows}
                            dataKey="_key"
                            size="small"
                            paginator
                            rows={15}
                            rowsPerPageOptions={[15, 30, 50]}
                        >
                            <Column className="text-right" header={translate("workspace")} field="name"/>
                            <Column
                                className="text-right"
                                header={translate("operations-health-status")}
                                body={(row) => <HealthTag value={row._health}/>}
                            />
                            <Column className="text-right" header={translate("operations-health-score")} field="_healthScore"/>
                            <Column className="text-right" header={translate("members-count")} field="_memberships"/>
                            <Column
                                className="text-right"
                                header={translate("operations-health-issues")}
                                body={(row) => (
                                    <div className="flex flex-wrap gap-1">
                                        {row._issues.slice(0, 2).map((issue) => (
                                            <Tag
                                                key={issue.code}
                                                className="text-xs"
                                                severity={issue.severity === "critical" ? "danger" : "warning"}
                                                value={translate(`operations-health-issue-${issue.code}`)}
                                            />
                                        ))}
                                        {row._issues.length > 2 && (
                                            <Tag className="text-xs" value={`+${row._issues.length - 2}`}/>
                                        )}
                                        {!row._issues.length && <HealthTag value="healthy"/>}
                                    </div>
                                )}
                            />
                            {(canOpenWorkspace || canPreviewRepair) && <Column
                                className="text-right"
                                header={translate("actions")}
                                body={(row) => (
                                    <div className="flex gap-1">
                                        {canOpenWorkspace && <Button
                                            icon="pi pi-eye"
                                            aria-label={translate("view")}
                                            className="text-xs gap-2"
                                            text
                                            rounded
                                            onClick={() => navigate(`/workspaces/${row.documentId || row.id}`)}
                                        />}
                                        {canPreviewRepair && <Button
                                            icon="pi pi-file-edit"
                                            aria-label={translate("operations-change-preview")}
                                            className="text-xs gap-2"
                                            text
                                            rounded
                                            disabled={!row._issues.length}
                                            onClick={() => setPreview(row)}
                                        />}
                                    </div>
                                )}
                            />}
                        </DataTable>
                    </DataState>
                </div>
            </TableCard>

            <Dialog
                visible={canPreviewRepair && Boolean(preview)}
                onHide={() => setPreview(null)}
                header={translate("operations-repair-preview-title")}
                className="rtl w-11 md:w-8 lg:w-6"
                modal
                draggable={false}
                pt={{root: {className: "rtl"}, content: {className: "text-xs"}}}
            >
                <div className="flex flex-column gap-3">
                    <div className="flex flex-column gap-1">
                        <span className="text-base font-bold">{preview?.name}</span>
                        <span className="text-xs text-500 ltr">{preview?.documentId}</span>
                    </div>
                    {(preview?._issues || []).map((issue) => (
                        <div key={issue.code} className="p-3 border-1 border-200 border-round flex flex-column gap-2">
                            <div className="flex align-items-center gap-2">
                                <SeverityTag value={issue.severity}/>
                                <span className="text-xs font-bold">
                                    {translate(`operations-health-issue-${issue.code}`)}
                                </span>
                            </div>
                            <span className="text-xs text-600">
                                {translate(`operations-health-action-${issue.code}`)}
                            </span>
                        </div>
                    ))}
                    <Message
                        className="w-full text-xs"
                        severity="info"
                        text={translate("operations-repair-api-required")}
                        pt={{text: {className: "text-xs"}}}
                    />
                </div>
            </Dialog>
        </div>
    );
}

function FinanceOperationsTab() {
    const translate = useTranslate();
    const navigate = useNavigate();
    const adminUser = useSelector((state) => state.auth.user);
    const canOpenSales = hasAdminPermission(adminUser, ADMIN_PERMISSIONS.SALES_ACCESS);
    const orders = useContentTypeData(CONTENT_TYPE_UIDS.ORDER);
    const invoices = useContentTypeData(CONTENT_TYPE_UIDS.INVOICE);
    const payments = useContentTypeData(CONTENT_TYPE_UIDS.PAYMENT);
    const subscriptions = useContentTypeData(CONTENT_TYPE_UIDS.SUBSCRIPTION);
    const manualPayments = useContentTypeData(CONTENT_TYPE_UIDS.MANUAL_PAYMENT_REQUEST);
    const controls = useMemo(
        () => [
            {ensure: orders.ensure, reload: orders.reload},
            {ensure: invoices.ensure, reload: invoices.reload},
            {ensure: payments.ensure, reload: payments.reload},
            {ensure: subscriptions.ensure, reload: subscriptions.reload},
            {ensure: manualPayments.ensure, reload: manualPayments.reload},
        ],
        [
            invoices.ensure,
            invoices.reload,
            manualPayments.ensure,
            manualPayments.reload,
            orders.ensure,
            orders.reload,
            payments.ensure,
            payments.reload,
            subscriptions.ensure,
            subscriptions.reload,
        ]
    );
    const loadCollections = useCollectionLoader(controls);
    const issues = useMemo(() => buildFinancialDiagnostics({
        orders: orders.status === "succeeded" ? orders.items : null,
        invoices: invoices.status === "succeeded" ? invoices.items : null,
        payments: payments.status === "succeeded" ? payments.items : null,
        subscriptions: subscriptions.status === "succeeded" ? subscriptions.items : null,
        manualPayments: manualPayments.status === "succeeded" ? manualPayments.items : null,
    }), [
        invoices.items,
        invoices.status,
        manualPayments.items,
        manualPayments.status,
        orders.items,
        orders.status,
        payments.items,
        payments.status,
        subscriptions.items,
        subscriptions.status,
    ]);
    const successfulPayments = payments.status === "succeeded"
        ? statusCount(payments.items, "paymentStatus", ["succeeded", "completed"])
        : null;
    const failedPayments = payments.status === "succeeded"
        ? statusCount(payments.items, "paymentStatus", "failed")
        : null;
    const pendingManualPayments = manualPayments.status === "succeeded"
        ? statusCount(
            manualPayments.items,
            "manualPaymentRequestStatus",
            ["submitted", "under_review"]
        )
        : null;
    const activeSubscriptions = subscriptions.status === "succeeded"
        ? subscriptions.items.filter(isCurrentSubscription).length
        : null;
    const loading = orders.loading
        || invoices.loading
        || payments.loading
        || subscriptions.loading
        || manualPayments.loading;
    const errors = [
        orders.error,
        invoices.error,
        payments.error,
        subscriptions.error,
        manualPayments.error,
    ].filter(Boolean);

    return (
        <div className="flex flex-column gap-3">
            <CollectionErrors errors={errors}/>
            <div className="grid">
                <StatCard label={translate("operations-finance-successful-payments")} value={successfulPayments ?? "-"} icon="pi pi-check-circle" tone="green"/>
                <StatCard label={translate("operations-finance-failed-payments")} value={failedPayments ?? "-"} icon="pi pi-times-circle" tone="red"/>
                <StatCard label={translate("operations-finance-active-subscriptions")} value={activeSubscriptions ?? "-"} icon="pi pi-id-card" tone="blue"/>
                <StatCard label={translate("operations-finance-pending-manual")} value={pendingManualPayments ?? "-"} icon="pi pi-clock" tone="orange"/>
            </div>
            <TableCard>
                <div className="p-3 flex flex-column gap-3">
                    <SalesSectionHeader
                        title={translate("operations-reconciliation-title")}
                        description={translate("operations-reconciliation-description")}
                        actions={(
                            <div className="flex gap-2">
                                {canOpenSales && <Button
                                    icon="pi pi-wallet"
                                    label={translate("operations-open-finance")}
                                    className="text-xs gap-2"
                                    outlined
                                    onClick={() => navigate("/sales")}
                                />}
                                <Button
                                    icon="pi pi-refresh"
                                    label={translate("refresh")}
                                    className="text-xs gap-2"
                                    text
                                    loading={loading}
                                    onClick={() => loadCollections(true)}
                                />
                            </div>
                        )}
                    />
                    <DataState loading={loading} error={null} empty={!issues.length}>
                        <DataTable
                            {...RTL_DATATABLE_PROPS}
                            value={issues}
                            dataKey="reference"
                            size="small"
                            paginator
                            rows={15}
                        >
                            <Column
                                className="text-right"
                                header={translate("operations-severity")}
                                body={(row) => <SeverityTag value={row.severity}/>}
                            />
                            <Column
                                className="text-right"
                                header={translate("operations-finance-diagnostic")}
                                body={(row) => translate(`operations-finance-issue-${row.code}`)}
                            />
                            <Column
                                className="text-right"
                                header={translate("workspace")}
                                body={(row) => entityLabel(row.workspace)}
                            />
                            <Column className="text-right ltr" header={translate("operations-reference")} field="reference"/>
                        </DataTable>
                    </DataState>
                </div>
            </TableCard>
            <TableCard>
                <div className="p-3 flex flex-column gap-3">
                    <SalesSectionHeader
                        title={translate("operations-failed-payments-title")}
                        description={translate("operations-failed-payments-description")}
                    />
                    <DataState
                        loading={loading}
                        error={null}
                        empty={!payments.items.some((payment) => payment.paymentStatus === "failed")}
                    >
                        <DataTable
                            {...RTL_DATATABLE_PROPS}
                            value={payments.items.filter((payment) => payment.paymentStatus === "failed")}
                            dataKey="documentId"
                            size="small"
                            paginator
                            rows={10}
                        >
                            <Column className="text-right ltr" header={translate("sales-transaction-number")} field="transactionNumber"/>
                            <Column className="text-right" header={translate("workspace")} body={(row) => entityLabel(row.workspace)}/>
                            <Column className="text-right ltr" header={translate("sales-amount")} body={(row) => formatMoney(row.amount)}/>
                            <Column
                                className="text-right"
                                header={translate("sales-status")}
                                body={(row) => (
                                    <Tag
                                        className="text-xs"
                                        severity={statusSeverity(row.paymentStatus)}
                                        value={translate(`sales-status-${String(row.paymentStatus).replace(/_/g, "-")}`)}
                                    />
                                )}
                            />
                            <Column className="text-right ltr" header={translate("sales-failure-code")} field="failureCode"/>
                        </DataTable>
                    </DataState>
                </div>
            </TableCard>
        </div>
    );
}

function AiOperationsTab() {
    const translate = useTranslate();
    const navigate = useNavigate();
    const adminUser = useSelector((state) => state.auth.user);
    const canOpenAiAdmin = hasAdminPermission(adminUser, ADMIN_PERMISSIONS.AI_ACCESS);
    const runs = useContentTypeData(CONTENT_TYPE_UIDS.AI_RUN);
    const settings = useContentTypeData(CONTENT_TYPE_UIDS.WORKSPACE_AI_SETTING);
    const wallets = useContentTypeData(CONTENT_TYPE_UIDS.CREDIT_WALLET);
    const budgetPolicies = useContentTypeData(CONTENT_TYPE_UIDS.AI_BUDGET_POLICY);
    const ratePolicies = useContentTypeData(CONTENT_TYPE_UIDS.AI_RATE_LIMIT_POLICY);
    const controls = useMemo(
        () => [
            {ensure: runs.ensure, reload: runs.reload},
            {ensure: settings.ensure, reload: settings.reload},
            {ensure: wallets.ensure, reload: wallets.reload},
            {ensure: budgetPolicies.ensure, reload: budgetPolicies.reload},
            {ensure: ratePolicies.ensure, reload: ratePolicies.reload},
        ],
        [
            budgetPolicies.ensure,
            budgetPolicies.reload,
            ratePolicies.ensure,
            ratePolicies.reload,
            runs.ensure,
            runs.reload,
            settings.ensure,
            settings.reload,
            wallets.ensure,
            wallets.reload,
        ]
    );
    const loadCollections = useCollectionLoader(controls);
    const runsAvailable = runs.status === "succeeded";
    const settingsAvailable = settings.status === "succeeded";
    const walletsAvailable = wallets.status === "succeeded";
    const failedRuns = runsAvailable
        ? runs.items.filter((run) => run.runStatus === "failed")
        : [];
    const retryableRuns = failedRuns.filter((run) => run.retryable);
    const runningRuns = runsAvailable
        ? statusCount(runs.items, "runStatus", ["queued", "running"])
        : null;
    const pausedSettings = settingsAvailable
        ? settings.items.filter(
            (setting) => !setting.aiEnabled || setting.creditMode === "paused"
        ).length
        : null;
    const frozenWallets = walletsAvailable
        ? statusCount(wallets.items, "creditWalletStatus", "frozen")
        : null;
    const pausedWorkspaces = pausedSettings !== null && frozenWallets !== null
        ? pausedSettings + frozenWallets
        : null;
    const loading = runs.loading
        || settings.loading
        || wallets.loading
        || budgetPolicies.loading
        || ratePolicies.loading;
    const errors = [
        runs.error,
        settings.error,
        wallets.error,
        budgetPolicies.error,
        ratePolicies.error,
    ].filter(Boolean);

    return (
        <div className="flex flex-column gap-3">
            <CollectionErrors errors={errors}/>
            <div className="grid">
                <StatCard label={translate("operations-ai-failed-runs")} value={runsAvailable ? failedRuns.length : "-"} icon="pi pi-times-circle" tone="red"/>
                <StatCard label={translate("operations-ai-retryable-runs")} value={runsAvailable ? retryableRuns.length : "-"} icon="pi pi-refresh" tone="orange"/>
                <StatCard label={translate("operations-ai-running-runs")} value={runningRuns ?? "-"} icon="pi pi-spin pi-spinner" tone="blue"/>
                <StatCard label={translate("operations-ai-paused-workspaces")} value={pausedWorkspaces ?? "-"} icon="pi pi-pause-circle" tone="orange"/>
            </div>
            <TableCard>
                <div className="p-3 flex flex-column gap-3">
                    <SalesSectionHeader
                        title={translate("operations-ai-failed-runs-title")}
                        description={translate("operations-ai-failed-runs-description")}
                        actions={(
                            <div className="flex gap-2">
                                {canOpenAiAdmin && <Button
                                    icon="pi pi-sparkles"
                                    label={translate("operations-open-ai-admin")}
                                    className="text-xs gap-2"
                                    outlined
                                    onClick={() => navigate("/ai-admin")}
                                />}
                                <Button
                                    icon="pi pi-refresh"
                                    label={translate("refresh")}
                                    className="text-xs gap-2"
                                    text
                                    loading={loading}
                                    onClick={() => loadCollections(true)}
                                />
                            </div>
                        )}
                    />
                    <DataState loading={loading} error={null} empty={!failedRuns.length}>
                        <DataTable
                            {...RTL_DATATABLE_PROPS}
                            value={failedRuns}
                            dataKey="documentId"
                            size="small"
                            paginator
                            rows={15}
                        >
                            <Column className="text-right" header={translate("workspace")} body={(row) => entityLabel(row.workspace)}/>
                            <Column className="text-right ltr" header={translate("operations-ai-engine")} field="engineKey"/>
                            <Column className="text-right ltr" header={translate("operations-ai-error-code")} field="errorCode"/>
                            <Column
                                className="text-right"
                                header={translate("operations-ai-retryable")}
                                body={(row) => (
                                    <Tag
                                        className="text-xs"
                                        severity={row.retryable ? "warning" : "secondary"}
                                        value={translate(row.retryable ? "yes" : "no")}
                                    />
                                )}
                            />
                            <Column className="text-right ltr" header={translate("operations-ai-duration")} field="durationMs"/>
                            <Column
                                className="text-right"
                                header={translate("createdAt")}
                                body={(row) => row.createdAt
                                    ? parseDate(row.createdAt, "iso", "YYYY/MM/DD HH:mm", "gregorian", "en")
                                    : "-"
                                }
                            />
                        </DataTable>
                    </DataState>
                </div>
            </TableCard>
        </div>
    );
}

function ActivityTab() {
    const translate = useTranslate();
    const activities = useContentTypeData(CONTENT_TYPE_UIDS.USER_EVENT_LOG);
    const ensure = activities.ensure;
    const reload = activities.reload;
    const [query, setQuery] = useState("");

    const load = useCallback(async (force = false) => {
        const request = force ? reload : ensure;
        await request(RECENT_QUERY);
    }, [ensure, reload]);

    useEffect(() => {
        load().catch(() => undefined);
    }, [load]);

    const normalizedQuery = query.trim().toLowerCase();
    const rows = activities.items
        .map((activity) => ({
            ...activity,
            _key: entityId(activity),
            _owner: entityLabel(activity.owner),
            _workspace: entityLabel(activity.workspace),
        }))
        .filter((activity) => !normalizedQuery || [
            activity.title,
            activity.message,
            activity.eventKind,
            activity._owner,
            activity._workspace,
        ].some((value) => String(value || "").toLowerCase().includes(normalizedQuery)));

    return (
        <TableCard>
            <div className="p-3 flex flex-column gap-3">
                <SalesSectionHeader
                    title={translate("operations-recent-activity")}
                    description={translate("operations-recent-activity-description")}
                    actions={(
                        <Button
                            icon="pi pi-refresh"
                            label={translate("refresh")}
                            className="text-xs gap-2"
                            text
                            loading={activities.loading}
                            onClick={() => load(true)}
                        />
                    )}
                />
                <InputText
                    className="w-full md:w-24rem text-xs"
                    value={query}
                    placeholder={translate("operations-activity-search")}
                    onChange={(event) => setQuery(event.target.value)}
                />
                <DataState loading={activities.loading} error={activities.error} empty={!rows.length}>
                    <DataTable
                        {...RTL_DATATABLE_PROPS}
                        value={rows}
                        dataKey="_key"
                        size="small"
                        paginator
                        rows={20}
                        rowsPerPageOptions={[20, 50, 100]}
                    >
                        <Column className="text-right" header={translate("title")} field="title"/>
                        <Column className="text-right" header={translate("message")} field="message"/>
                        <Column className="text-right" header={translate("user")} field="_owner"/>
                        <Column className="text-right" header={translate("workspace")} field="_workspace"/>
                        <Column
                            className="text-right"
                            header={translate("operations-priority")}
                            body={(row) => (
                                <Tag
                                    className="text-xs"
                                    severity={row.priority === "urgent" ? "danger" : row.priority === "high" ? "warning" : "secondary"}
                                    value={translate(row.priority || "normal")}
                                />
                            )}
                        />
                        <Column
                            className="text-right"
                            header={translate("createdAt")}
                            body={(row) => row.createdAt
                                ? parseDate(row.createdAt, "iso", "YYYY/MM/DD HH:mm", "gregorian", "en")
                                : "-"
                            }
                        />
                    </DataTable>
                </DataState>
            </div>
        </TableCard>
    );
}

function Operations() {
    const translate = useTranslate();
    const {user} = useSelector((state) => state.auth);
    const [searchParams, setSearchParams] = useSearchParams();
    const canOpenOperations = hasOperationsAccess(user);
    const tabs = [
        ...(hasAdminPermission(user, ADMIN_PERMISSIONS.OPERATIONS_TAB_OVERVIEW)
            ? [{key: "overview", labelKey: "operations-tab-overview", helpKey: "operations-tab-overview-help", component: OperationsOverview}]
            : []),
        ...(hasAdminPermission(user, ADMIN_PERMISSIONS.OPERATIONS_TAB_HEALTH)
            ? [{key: "health", labelKey: "operations-tab-workspace-health", helpKey: "operations-tab-workspace-health-help", component: WorkspaceHealthTab}]
            : []),
        ...(hasAdminPermission(user, ADMIN_PERMISSIONS.OPERATIONS_TAB_FINANCE)
            ? [{key: "finance", labelKey: "operations-tab-finance", helpKey: "operations-tab-finance-help", component: FinanceOperationsTab}]
            : []),
        ...(hasAdminPermission(user, ADMIN_PERMISSIONS.OPERATIONS_TAB_AI)
            ? [{key: "ai", labelKey: "operations-tab-ai", helpKey: "operations-tab-ai-help", component: AiOperationsTab}]
            : []),
        ...(hasAdminPermission(user, ADMIN_PERMISSIONS.OPERATIONS_TAB_ACTIVITY)
            ? [{key: "activity", labelKey: "operations-tab-activity", helpKey: "operations-tab-activity-help", component: ActivityTab}]
            : []),
    ];
    const requestedTab = searchParams.get("tab");
    const requestedTabIndex = tabs.findIndex((tab) => tab.key === requestedTab);
    const [activeIndex, setActiveIndex] = useState(
        requestedTabIndex >= 0 ? requestedTabIndex : 0
    );
    const openTab = (key) => {
        const nextIndex = tabs.findIndex((tab) => tab.key === key);
        if (nextIndex < 0) return;
        setActiveIndex(nextIndex);
        setSearchParams({tab: key});
    };
    const activeTab = tabs[activeIndex] || tabs[0];
    const ActiveComponent = activeTab?.component;

    if (!canOpenOperations) {
        return <AdminAccessDenied
            titleKey="operations-access-denied-title"
            descriptionKey="operations-access-denied-description"
        />;
    }

    if (!ActiveComponent) {
        return <AdminAccessDenied/>;
    }

    return (
        <div className="w-full p-3 flex flex-column gap-3 rtl text-xs">
            <PageHeaderButton
                title="operations-center"
                description="operations-center-description"
            />
            <Message
                className="w-full text-xs"
                severity="info"
                text={translate("operations-readonly-contract-notice")}
                pt={{text: {className: "text-xs"}}}
            />
            <TabView
                activeIndex={activeIndex}
                onTabChange={(event) => openTab(tabs[event.index].key)}
                className="rtl text-xs"
                pt={{
                    nav: {className: "rtl flex-nowrap"},
                    panelContainer: {className: "p-3 border-1 border-200 border-round-bottom-xl"},
                }}
            >
                {tabs.map((tab, index) => (
                    <TabPanel
                        key={tab.key}
                        header={translate(tab.labelKey)}
                        pt={{headerAction: {className: "text-xs white-space-nowrap"}}}
                    >
                        {activeIndex === index && <>
                            <ActiveComponent onOpenTab={openTab}/>
                            <MarkdownHelp contentKey={tab.helpKey}/>
                        </>}
                    </TabPanel>
                ))}
            </TabView>
        </div>
    );
}

export {OperationsOverview};
export default Operations;
